/**
 * ConversationListItem - Single conversation in the list
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Conversation } from '../types/chat';
import { formatConversationTimestamp } from '../utils/formatTimestamp';

interface ConversationListItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}

export const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isActive,
  onClick,
}) => {
  const navigate = useNavigate();

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering conversation onClick
    navigate(`/friends/${conversation.friendUserId}`);
  };

  return (
    <div
      onClick={onClick}
      className={`flex items-center space-x-3 p-3 cursor-pointer transition-colors ${
        isActive ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'
      }`}
    >
      {/* Avatar (clickable to profile) */}
      <div className="relative flex-shrink-0">
        <button
          onClick={handleAvatarClick}
          className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
        >
          {conversation.friendAvatar ? (
            <img
              src={conversation.friendAvatar}
              alt={conversation.friendName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-base text-gray-600">
                {conversation.friendName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </button>
        {/* Red dot indicator for unread messages */}
        {conversation.unreadCount > 0 && (
          <div className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {conversation.friendName}
          </h4>
          {conversation.lastMessageAt && (
            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
              {formatConversationTimestamp(conversation.lastMessageAt)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {conversation.friendUsername && (
              <p className="text-xs text-gray-500 truncate">@{conversation.friendUsername}</p>
            )}
            {conversation.lastMessageSnippet && (
              <p className="text-sm text-gray-600 truncate mt-1">
                {conversation.lastMessageSnippet}
              </p>
            )}
          </div>
          {conversation.unreadCount > 0 && (
            <div className="flex-shrink-0 ml-2">
              <div className="bg-blue-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] px-1.5 flex items-center justify-center">
                {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

